import React, { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Avatar,
} from "@mui/material";
import { Stack, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FacebookIcon from "@mui/icons-material/Facebook";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import LinearProgress from "@mui/material/LinearProgress";
import moment from "moment";
import {
  getAllFacebookLeads,
  getFacebookLeadCampaigns,
  updateFacebookLeadStatus,
} from "../../api";
import Layout from "../../components/Layout";
import FacebookLeadDetailsModal from "../../components/FacebookLeadDetailsModal";

const AVATAR_COLORS = ["#5b8def", "#9c6ade", "#3cb17a", "#e2953d", "#e2586b", "#3fa7b3"];
const CAMPAIGN_COLORS = ["#1976d2", "#7b1fa2", "#2e7d32", "#ef6c00", "#c62828", "#00838f"];

function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash;
}

function getAvatarColor(name) {
  if (!name) return "#9e9e9e";
  return AVATAR_COLORS[hashString(name) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

function getCampaignColor(name) {
  if (!name) return "#757575";
  return CAMPAIGN_COLORS[hashString(name) % CAMPAIGN_COLORS.length];
}

const STATUS_OPTIONS = [
  { value: "new", label: "New", color: "#7c4dff" },
  { value: "hot", label: "Hot", color: "#ef6c00" },
  { value: "cold", label: "Cold", color: "#0288d1" },
  { value: "dead", label: "Dead", color: "#9e9e9e" },
  { value: "active", label: "Active", color: "#2e7d32" },
  { value: "converted", label: "Converted", color: "#1565c0" },
];

function getStatusOption(status) {
  return STATUS_OPTIONS.find((option) => option.value === status);
}

function Dot({ color }) {
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 7,
        height: 7,
        borderRadius: "50%",
        backgroundColor: color,
        mr: 1,
      }}
    />
  );
}

function EnhancedTableHead() {
  return (
    <TableHead>
      <TableRow>
        <TableCell>ID</TableCell>
        <TableCell>Name</TableCell>
        <TableCell align="center">Details</TableCell>
        <TableCell>Email</TableCell>
        <TableCell>Phone</TableCell>
        <TableCell>Campaign Name</TableCell>
        <TableCell>Status</TableCell>
        <TableCell>Date</TableCell>
      </TableRow>
    </TableHead>
  );
}

export default function FacebookLeads() {
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [leadsCount, setLeadsCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rowData, setRowData] = useState({});
  const [openModel, setOpenModel] = useState(false);
  const [leadStatuses, setLeadStatuses] = useState({});

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        setLoading(true);
        const data = await getAllFacebookLeads(
          page + 1,
          limit,
          search,
          campaignFilter,
          statusFilter
        );
        setLeads(data?.leads || []);
        setLeadsCount(data?.totalcount || 0);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };
    fetchLeads();
  }, [page, limit, search, campaignFilter, statusFilter]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const data = await getFacebookLeadCampaigns();
        setCampaigns(data?.campaigns || []);
      } catch (error) {
        console.log(error);
      }
    };
    fetchCampaigns();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearch(searchValue);
    setPage(0);
  };

  const handleCampaignFilterChange = (event) => {
    setCampaignFilter(event.target.value);
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleStatusChange = async (leadId, newStatus) => {
    setLeadStatuses((prev) => ({ ...prev, [leadId]: newStatus }));
    await updateFacebookLeadStatus(leadId, newStatus);
  };

  const handleModelOpen = useCallback((e, row) => {
    e.stopPropagation();
    setRowData(row);
    setOpenModel(true);
  }, []);

  const handleCloseModel = () => {
    setOpenModel(false);
    setRowData({});
  };

  return (
    <Layout>
      <Box bgcolor="#f0f0f1" minHeight="calc(100vh - 67px)" px={3} py={3}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={3}>
          <Avatar sx={{ bgcolor: "#1877F2", width: 40, height: 40 }}>
            <FacebookIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 21, fontWeight: 700, lineHeight: 1.3 }}>
              Facebook Leads
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#8a8a8a" }}>
              {leadsCount} total lead{leadsCount === 1 ? "" : "s"}
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction="row"
          flexWrap="wrap"
          gap={1.5}
          alignItems="center"
          justifyContent="flex-end"
          mb={2}
        >
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <Select
              displayEmpty
              value={campaignFilter}
              onChange={handleCampaignFilterChange}
              sx={{
                fontSize: "14px",
                borderRadius: "20px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
              }}
            >
              <MenuItem value="">
                <em>All Campaigns</em>
              </MenuItem>
              {campaigns.map((campaign, index) => (
                <MenuItem
                  key={campaign.id || index}
                  value={campaign.name || campaign}
                >
                  {campaign.name || campaign}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              displayEmpty
              value={statusFilter}
              onChange={handleStatusFilterChange}
              renderValue={(value) => {
                if (!value) return "All Statuses";
                const option = getStatusOption(value);
                return (
                  <Box display="flex" alignItems="center">
                    <Dot color={option?.color || "#c4c4c4"} />
                    {option?.label || value}
                  </Box>
                );
              }}
              sx={{
                fontSize: "14px",
                borderRadius: "20px",
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
              }}
            >
              <MenuItem value="">
                <em>All Statuses</em>
              </MenuItem>
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Dot color={option.color} />
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <form onSubmit={handleSearchSubmit}>
            <TextField
              size="small"
              placeholder="Search leads..."
              value={searchValue}
              onChange={handleSearch}
              sx={{
                width: "28ch",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "20px",
                  backgroundColor: "#fff",
                  "& fieldset": { borderColor: "#e0e0e0" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#9e9e9e" }} />
                  </InputAdornment>
                ),
              }}
            />
          </form>
        </Stack>

        <Paper
          variant="outlined"
          sx={{ width: "100%", borderRadius: 2, overflow: "hidden", borderColor: "#eee" }}
        >
          {loading && <LinearProgress />}
          <TableContainer>
            <Table
              size="small"
              aria-label="facebook leads table"
              sx={{
                "& .MuiTableCell-root": {
                  borderBottom: "1px solid #f0f0f0",
                  padding: "10px 12px",
                  whiteSpace: "nowrap",
                },
                "& thead .MuiTableCell-root": {
                  backgroundColor: "#fafafa",
                  color: "#616161",
                  fontWeight: 700,
                  fontSize: 14,
                },
              }}
            >
              <EnhancedTableHead />
              <TableBody>
                {leads.length > 0 ? (
                  leads.map((row, index) => (
                    <TableRow hover tabIndex={-1} key={row.id || index}>
                      <TableCell sx={{ color: "#9e9e9e" }}>{row.id}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.25}>
                          <Avatar
                            sx={{
                              width: 30,
                              height: 30,
                              fontSize: 12,
                              fontWeight: 700,
                              bgcolor: getAvatarColor(row.name),
                            }}
                          >
                            {getInitials(row.name)}
                          </Avatar>
                          <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {row.name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton onClick={(e) => handleModelOpen(e, row)}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ color: "#616161" }}>{row.email}</TableCell>
                      <TableCell sx={{ color: "#616161" }}>{row.phone_number}</TableCell>
                      <TableCell>
                        {row.campaign_name ? (
                          <Box display="flex" alignItems="center">
                            <Dot color={getCampaignColor(row.campaign_name)} />
                            <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                              {row.campaign_name}
                            </Typography>
                          </Box>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <Select
                          variant="standard"
                          disableUnderline
                          value={leadStatuses[row.id] ?? row.status ?? "new"}
                          onChange={(e) => handleStatusChange(row.id, e.target.value)}
                          renderValue={(value) => {
                            const option = getStatusOption(value);
                            return (
                              <Box display="flex" alignItems="center">
                                <Dot color={option?.color || "#c4c4c4"} />
                                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
                                  {option?.label || value}
                                </Typography>
                              </Box>
                            );
                          }}
                          sx={{ fontSize: "13px", minWidth: 100 }}
                        >
                          {STATUS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Dot color={option.color} />
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </TableCell>
                      <TableCell sx={{ color: "#616161" }}>
                        {row.created_at ? moment(row.created_at).format("ll") : "-"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No Facebook Lead(s) Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            {loading && <LinearProgress />}
          </TableContainer>
          <TablePagination
            component="div"
            count={leadsCount}
            rowsPerPageOptions={[10, 25, 50, 100]}
            rowsPerPage={limit}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            getItemAriaLabel={(type) =>
              type === "previous" ? "Go to previous page" : "Go to next page"
            }
            sx={{
              borderTop: "1px solid #f0f0f0",
              "& .MuiTablePagination-toolbar": { px: 3, py: 0.5, minHeight: 56 },
              "& .MuiTablePagination-selectLabel": {
                fontSize: 13.5,
                color: "#424242",
                fontWeight: 500,
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: 13.5,
                color: "#424242",
                fontWeight: 500,
              },
              "& .MuiTablePagination-select": {
                fontSize: 13.5,
                fontWeight: 700,
              },
              "& .MuiTablePagination-actions": { ml: 2 },
              "& .MuiTablePagination-actions .MuiIconButton-root": {
                border: "1px solid #e0e0e0",
                borderRadius: "50%",
                mx: 0.5,
                p: 1,
                "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#bdbdbd" },
                "&:focus-visible": { outline: "2px solid #1976d2", outlineOffset: 2 },
                "&.Mui-disabled": { borderColor: "#f0f0f0" },
              },
            }}
          />
        </Paper>
      </Box>
      <FacebookLeadDetailsModal
        openModel={openModel}
        handleClose={handleCloseModel}
        {...rowData}
      />
    </Layout>
  );
}
